import { skipToken } from "@reduxjs/toolkit/query";
import { getCompanyTimezone } from "@repo/app-types/entities/company";
import { selectPopulationFilters } from "@repo/app-types/entities/filters";
import { getOutcomeDefinitionFromPlaybook } from "@repo/app-types/entities/playbook";
import { asTeammateId } from "@repo/app-types/entities/teammate";
import type { TranscriptMatchesByGrading } from "@repo/app-types/entities/teammatePerformanceSummary";
import { PageHeaderFilterBarPortal } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import type { FC } from "react";
import { useMemo } from "react";
import { useParams } from "react-router";
import {
  usePlaybookTeammateMostRecentGradingsCountQuery,
  usePlaybookTeammateMostRecentGradingsInfiniteQuery,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { PageBody, TableCard } from "../../components/layout/pageLayout.js";
import { useAppSelector } from "../../hooks/hook.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../hooks/useModalNavigation.js";
import { selectCompany } from "../company/companySlice.js";
import {
  buildTeamPerformancePageQueryArgs,
  checkHasNarrowingFilters,
} from "../filters/buildFilterQueryArgs.js";
import { useFilters } from "../filters/filters.js";
import { useAgentPlaybookFilterDefaults } from "../filters/usePlaybookFilterDefaults.js";
import { buildCallActivityColumns } from "../teammatePerformance/callActivityColumns.js";
import {
  buildGradingModalPath,
  buildTranscriptSearchNavState,
} from "../teammatePerformance/gradingHelpers.js";
import { GradingsTable } from "../teammatePerformance/gradingsTable.js";
import { DisplayColumnsSelector } from "../teammatePerformance/hooks/displayColumnsSelector.js";
import { useCustomFieldColumnSelector } from "../teammatePerformance/hooks/useCustomFieldColumnSelector.js";
import { useTranscriptSearch } from "../teammatePerformance/hooks/useTranscriptSearch.js";

export const TeammateAgentCallsTab: FC = () => {
  const { teammateAgentId: teammateAgentIdParam } = useParams();
  if (!teammateAgentIdParam) {
    throw new Error("Missing teammateAgentId param.");
  }
  const teammateAgentId = asTeammateId(teammateAgentIdParam);

  const modalNavigation = useModalNavigation();
  const {
    selectedCustomFieldDefinitions,
    selectSections: customFieldSections,
    selectedIds: customFieldSelectedIds,
    onSelectionChange: onCustomFieldSelectionChange,
  } = useCustomFieldColumnSelector({
    includeDataExtractionFields: true,
    preferenceScope: { type: "teammateAgent", teammateAgentId },
  });

  const { data: teammateAgent, isLoading: isLoadingTeammateAgent } =
    useGetTeammateAgentQuery(teammateAgentId);

  const playbookId = teammateAgent?.teammate_agent_config.playbook_id;

  // Gated on the agent having ever been given a call task, not on outbound
  // being enabled: the columns should keep showing the history of a queue that
  // has since drained, and stay out of the way of agents that only take calls.
  const showsCallActivity = teammateAgent?.has_call_tasks === true;

  const company = useAppSelector(selectCompany);
  const companyTimezone = getCompanyTimezone(company);
  const callActivityColumns = useMemo(
    () =>
      showsCallActivity ? buildCallActivityColumns(companyTimezone) : undefined,
    [showsCallActivity, companyTimezone],
  );

  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);
  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

  const now = useNow();
  const filterDefaults = useAgentPlaybookFilterDefaults(playbookId, "calls");
  const [
    {
      dateRange,
      dateBasis,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
    },
    renderFilters,
    hasNarrowingChanges,
  ] = useFilters(
    [
      "dateRange",
      "dateBasis",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
    ],
    { playbookId, view: "calls" },
    filterDefaults,
  );

  const { search, renderSearchInput, deepSearchEnabled } =
    useTranscriptSearch();
  const deepSearchEnabledArg = search ? deepSearchEnabled : undefined;

  const baseArgs =
    playbookId !== undefined
      ? buildTeamPerformancePageQueryArgs(now, {
          playbookId,
          dateRange,
          dateBasis,
          teammateGroupIds: null,
          outcome,
          customFields,
          playbookConditions,
          signalId,
          signalMetadataFields,
          totalRecordingDuration,
        })
      : null;

  // The "N of M" denominator: the same population the list is drawn from (so
  // it follows "Include calls without grades") minus the user's own filters.
  // Suppressed unless the user narrowed something that reaches the filters,
  // where the two queries would be identical.
  const showsDenominator =
    baseArgs !== null &&
    hasNarrowingChanges &&
    checkHasNarrowingFilters(baseArgs.filters);

  const populationArgs =
    baseArgs && playbookId !== undefined && showsDenominator
      ? {
          playbookId,
          teammateId: teammateAgentId,
          scope: baseArgs.scope,
          filters: selectPopulationFilters(baseArgs.filters),
          search,
          deepSearchEnabled: deepSearchEnabledArg,
        }
      : skipToken;

  const filteredArgs =
    baseArgs && playbookId !== undefined
      ? {
          playbookId,
          teammateId: teammateAgentId,
          scope: baseArgs.scope,
          filters: baseArgs.filters,
          search,
          deepSearchEnabled: deepSearchEnabledArg,
        }
      : skipToken;

  // `currentData`, not `data`: the latter falls back to the previous args'
  // result whenever the current request isn't successful, which for the
  // denominator means a failed refetch can leave M behind N ("80 of 45").
  const {
    currentData: totalGradingCount,
    isFetching: isFetchingTotalGradingCount,
  } = usePlaybookTeammateMostRecentGradingsCountQuery(populationArgs);

  const { data: gradingCount, isFetching: isFetchingGradingCount } =
    usePlaybookTeammateMostRecentGradingsCountQuery(filteredArgs);

  const {
    data: gradingsData,
    isFetching: isFetchingGradings,
    isError: isErrorGradings,
    hasNextPage: canFetchMoreGradings,
    fetchNextPage: fetchMoreGradings,
    isFetchingNextPage: isFetchingMoreGradings,
  } = usePlaybookTeammateMostRecentGradingsInfiniteQuery(filteredArgs);

  const gradings = useMemo(
    () => gradingsData?.pages.flatMap((page) => page.recentGradings) ?? [],
    [gradingsData],
  );

  const snippetsByGradingId = useMemo(
    () =>
      Object.assign(
        {},
        ...(gradingsData?.pages.map((page) => page.snippets) ?? []),
      ) as TranscriptMatchesByGrading,
    [gradingsData],
  );

  // True while the gradings count/list is loading the full result set — the
  // initial fetch or a refetch from a filter/search change — but not while
  // appending more pages (`isFetchingMoreGradings`).
  const isLoadingGradings =
    isFetchingTotalGradingCount ||
    isFetchingGradingCount ||
    (isFetchingGradings && !isFetchingMoreGradings);

  if (isLoadingTeammateAgent || !teammateAgent || !playbookId) {
    return null;
  }

  return (
    <>
      <PageHeaderFilterBarPortal>
        {renderFilters({
          isLoading: isLoadingGradings,
          // Explicitly gated rather than relying on the skipped query: RTK
          // Query's `data` keeps serving the last successful result once args
          // become `skipToken`, which would strand a stale denominator.
          denominatorCount: showsDenominator ? totalGradingCount : undefined,
          count: gradingCount,
          leadingItems: renderSearchInput({
            resultCount: gradingCount,
            isLoadingResults: isLoadingGradings,
          }),
          rightActions: (
            <DisplayColumnsSelector
              customFieldSections={customFieldSections}
              customFieldSelectedIds={customFieldSelectedIds}
              onCustomFieldSelectionChange={onCustomFieldSelectionChange}
            />
          ),
        })}
      </PageHeaderFilterBarPortal>
      <PageBody>
        <TableCard>
          <GradingsTable
            outcomeDefinition={outcomeDefinition}
            gradings={gradings}
            snippetsByGradingId={snippetsByGradingId}
            isLoading={isLoadingGradings}
            isError={isErrorGradings}
            onLoadMore={canFetchMoreGradings ? fetchMoreGradings : undefined}
            onGradingAction={(grading) => {
              const gradingSnippets = snippetsByGradingId[grading.id];
              modalNavigation.push(
                buildGradingModalPath(grading.id, search, gradingSnippets?.[0]),
                search?.trim() && gradingSnippets?.length
                  ? buildTranscriptSearchNavState(search, gradingSnippets)
                  : undefined,
              );
            }}
            selectedCustomFieldDefinitions={selectedCustomFieldDefinitions}
            additionalColumns={callActivityColumns}
            showsPlaybookColumn={false}
            isFullBleed
          />
        </TableCard>
      </PageBody>

      <NavigationSlideoverContainer />
    </>
  );
};
